<template>
    <div class="login-page">
        <div class="login-card-wrapper">
            <PageCard class="login-card">
                <div class="login-inner">
                    <!-- Logo -->
                    <div class="login-logo">
                        <img v-if="tenant && tenant.logo" :src="tenant.logo" class="tenant-logo" alt="Tenant Logo" />
                        <div v-else class="brand-logo-block">
                            <img src="@/assets/logo.png" class="brand-logo" alt="Swift HRMS" />
                            <div class="brand-tagline">Efficient. Dynamic. Human-Centric.</div>
                        </div>
                    </div>

                    <!-- Title -->
                    <div class="login-title" v-if="tenant && isMultiTenant">
                        {{ tenant.name }}
                    </div>
                    <div class="login-title" v-else-if="currentStep === 'workspace'">
                        Find your Workspace
                    </div>
                    <p class="login-subtitle" v-if="currentStep === 'workspace'">
                        Enter your organization's code to get started
                    </p>
                    <p class="login-subtitle" v-else>
                        Sign in to continue to your dashboard
                    </p>

                    <form @submit.prevent="handleSubmit">
                        
                        <!-- Step 1: Company Code / Workspace Detection -->
                        <div v-if="currentStep === 'workspace'" class="login-form-section">
                            <div class="form-group-modern">
                                <label for="subdomain" class="form-label-modern">Workspace Code</label>
                                <div class="input-modern-wrapper">
                                    <i class="fas fa-building input-icon"></i>
                                    <input
                                        id="subdomain"
                                        type="text"
                                        class="input-modern"
                                        placeholder="e.g. acme"
                                        v-model="subdomain"
                                        required
                                        autofocus
                                    />
                                </div>
                            </div>

                            <div class="mt-4">
                                <Button type="submit" class="login-btn-full">
                                    Continue <i class="fas fa-arrow-right ms-2"></i>
                                </Button>
                            </div>
                        </div>

                        <!-- Step 2: Login Credentials -->
                        <div v-else class="login-form-section">
                            <div class="form-group-modern">
                                <label for="username" class="form-label-modern">Username or Email</label>
                                <div class="input-modern-wrapper">
                                    <i class="fas fa-user input-icon"></i>
                                    <input
                                        id="username"
                                        type="text"
                                        class="input-modern"
                                        placeholder="Enter your username or email"
                                        v-model="username"
                                        required
                                        autocomplete="username"
                                        autofocus
                                    />
                                </div>
                            </div>

                            <div class="form-group-modern">
                                <label for="password" class="form-label-modern">Password</label>
                                <div class="input-modern-wrapper">
                                    <i class="fas fa-lock input-icon"></i>
                                    <input
                                        id="password"
                                        type="password"
                                        placeholder="Enter your password"
                                        class="input-modern"
                                        v-model="password"
                                        required
                                        autocomplete="current-password"
                                    />
                                </div>
                            </div>

                            <div class="d-flex justify-content-end mb-3">
                                <a href="/forgot-password" class="forgot-link">Forgot Password?</a>
                            </div>

                            <div>
                                <Button type="submit" class="login-btn-full">
                                    {{ processingForm ? "Signing in..." : "Sign In" }}
                                </Button>
                            </div>

                            <div v-if="isMultiTenant" class="switch-workspace">
                                <a href="#" @click.prevent="resetWorkspace">
                                    <i class="fas fa-exchange-alt me-1"></i> Switch workspace
                                </a>
                            </div>
                        </div>
                    </form>
                </div>
            </PageCard>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex"
import Button from "@/components/UI/Button"
import { errMsg } from "../../utils/common"
import PageCard from "./PageCard.vue"

export default {
    data() {
        return {
            username: "",
            password: "",
            subdomain: "",
            processingForm: false,
        }
    },

    components: { PageCard, Button },
    computed: {
        ...mapState("auth", ["tenant"]),
        isMultiTenant() {
            return process.env.VUE_APP_MULTI_TENANT_ENABLED === "true"
        },
        currentStep() {
            // If multi-tenant is off, skip to login
            if (!this.isMultiTenant) return "login"
            // If we have tenant info (logo, name, etc), we are in login step
            return this.tenant ? "login" : "workspace"
        }
    },
    methods: {
        ...mapActions("auth", ["login", "fetchTenant", "detectTenant"]),
        
        resetWorkspace() {
             this.$store.commit("auth/SET_TENANT", null)
             this.subdomain = ""
        },

        async handleSubmit() {
            console.log("Form submitted with", { subdomain: this.subdomain, username: this.username })
            if (this.processingForm) return

            // STEP 1: Process Workspace
            if (this.currentStep === "workspace") {
                 if (!this.subdomain) {
                     return this.$toast.error("Please enter your company code")
                 }
                 this.processingForm = true
                 try {
                     await this.fetchTenant(this.subdomain)
                     this.$toast.success("Workspace identified!")
                 } catch (err) {
                     console.error("Fetch Tenant err", err)
                     this.$toast.error(errMsg(err, "Workspace not found!"))
                 } finally {
                     this.processingForm = false
                 }
                 return
            }

            // STEP 2: Process Login
            if (!this.username || !this.password) {
                return this.$toast.error("Please enter your credentials")
            }
            this.processingForm = true
            try {
                await this.login({
                    username: this.username,
                    password: this.password,
                    // Note: http.js will automatically pick up the subdomain header 
                    // from localStorage (via SET_TENANT mutation)
                })
                this.$toast.success("Logged in successfully!")
                
                // Full reload to /dashboard so all components mount fresh with auth state
                // Tenant subdomain is preserved via sessionStorage
                window.location.href = "/dashboard"
            } catch (err) {
                console.log("Login err", err)
                this.$toast.error(errMsg(err, "Could not login. Please check your credentials."))
            } finally {
                this.processingForm = false
            }
        },
    },
    mounted() {
        // Automatically detect if there's already a saved workspace in localStorage
        this.detectTenant()
    }
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.login-page {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 20px;
}
.login-card-wrapper {
    width: 100%;
    max-width: 440px;
    animation: fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}
@keyframes fadeUp {
    from {
        opacity: 0;
        transform: translateY(16px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
.login-card {
    border: 1px solid $gray200 !important;
}
.login-inner {
    text-align: center;
}
.login-logo {
    margin-bottom: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
}
.tenant-logo {
    max-height: 60px;
    max-width: 180px;
    object-fit: contain;
}
.brand-logo-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
}
.brand-logo {
    width: 120px;
    height: auto;
    object-fit: contain;
    filter: drop-shadow(0 2px 8px rgba(0, 94, 137, 0.12));
}
.brand-tagline {
    font-size: 11px;
    font-weight: 600;
    color: $gray400;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}
.default-logo {
    width: 72px;
    height: 72px;
    object-fit: contain;
    padding: 12px;
    background: $gray50;
    border: 1px solid $gray200;
    border-radius: $radiusMd;
}
.login-title {
    font-size: 20px;
    font-weight: 700;
    color: $gray900;
    margin-bottom: 4px;
    letter-spacing: -0.02em;
}
.login-subtitle {
    font-size: 13px;
    color: $gray500;
    margin-bottom: 28px;
}
.login-form-section {
    text-align: left;
}
.form-group-modern {
    margin-bottom: 18px;
}
.form-label-modern {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: $gray700;
    margin-bottom: 6px;
}
.input-modern-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}
.input-icon {
    position: absolute;
    left: 14px;
    color: $gray400;
    font-size: 14px;
    pointer-events: none;
    transition: color $transitionFast;
}
.input-modern {
    width: 100%;
    padding: 11px 14px 11px 40px;
    border: 1px solid $gray300;
    border-radius: $radiusSm;
    font-size: 14px;
    color: $gray800;
    background: $gray50;
    transition: all $transitionFast;
    outline: none;
    font-family: inherit;

    &::placeholder {
        color: $gray400;
    }
    &:focus {
        border-color: $primaryColor;
        box-shadow: 0 0 0 3px rgba(0, 94, 137, 0.1);
        background: white;

        & + .input-icon,
        ~ .input-icon {
            color: $primaryColor;
        }
    }
}
// Fix: icon color on focus (icon is before input in DOM)
.input-modern-wrapper:focus-within .input-icon {
    color: $primaryColor;
}

.login-btn-full {
    width: 100% !important;
    min-width: unset !important;
}
.forgot-link {
    font-size: 13px;
    color: $primaryColor;
    font-weight: 500;
    transition: color $transitionFast;

    &:hover {
        color: $primaryDark;
    }
}
.switch-workspace {
    text-align: center;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid $gray200;

    a {
        font-size: 13px;
        color: $gray500;
        font-weight: 500;
        transition: color $transitionFast;

        &:hover {
            color: $primaryColor;
        }
    }
}

/* ---- Tablet ---- */
@media (max-width: 768px) {
    .login-card-wrapper {
        max-width: 400px;
    }
}

/* ---- Mobile ---- */
@media (max-width: 480px) {
    .login-page {
        padding: 16px;
        align-items: flex-start;
        padding-top: 10vh;
    }
    .login-card-wrapper {
        max-width: 100%;
    }
    .login-title {
        font-size: 18px;
    }
    .login-subtitle {
        font-size: 12px;
        margin-bottom: 20px;
    }
    .input-modern {
        font-size: 16px; /* Prevents iOS auto-zoom on focus */
        padding: 12px 14px 12px 38px;
    }
    .default-logo {
        width: 60px;
        height: 60px;
    }
}

@media (max-width: 576px) {
    .login-card-wrapper {
        max-width: 100%;
    }
}
</style>